/**
 * Global Section Lazy Loader & Performance Optimizer
 * Lazily renders off-screen sections and defers heavy DOM/script execution
 * until user scrolls near the viewport.
 */
(function () {
  'use strict';

  // If in Shopify Theme Customizer design mode, load everything immediately
  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  function initSectionLazyLoader() {
    const mainContent = document.getElementById('MainContent') || document.querySelector('main');
    if (!mainContent) return;

    const sections = Array.from(mainContent.querySelectorAll('.shopify-section'));
    if (!sections.length) return;

    // The very first section (Hero / Banner above fold) is loaded immediately for optimal LCP
    const firstSection = sections[0];
    if (firstSection) {
      firstSection.setAttribute('data-section-loaded', 'true');
      firstSection.classList.add('section--loaded');
    }

    if (isDesignMode || !('IntersectionObserver' in window)) {
      // In design mode or fallback, mark all sections loaded
      sections.forEach((section) => {
        section.setAttribute('data-section-loaded', 'true');
        section.classList.add('section--loaded');
      });
      return;
    }

    // Observer with 400px preload margin before entering viewport
    const sectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          const target = entry.target;
          if (target instanceof HTMLElement) {
            loadSection(target);
          }
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '400px 0px 400px 0px',
      threshold: 0
    });

    // Observe all below-the-fold sections
    for (let i = 1; i < sections.length; i++) {
      const section = sections[i];
      if (section && section.getAttribute('data-section-loaded') !== 'true') {
        section.setAttribute('data-section-loaded', 'false');
        section.classList.add('section--lazy');
        sectionObserver.observe(section);
      }
    }
  }

  /**
   * @param {HTMLElement} section
   */
  function loadSection(section) {
    if (!section || section.getAttribute('data-section-loaded') === 'true') return;

    const sectionId = section.id || section.dataset.sectionId || section.className || '';

    section.setAttribute('data-section-loaded', 'true');
    requestAnimationFrame(() => {
      section.classList.remove('section--lazy');
      section.classList.add('section--loaded');
    });

    // Trigger custom events for sections to initialize components (sliders, Boost AI, etc.)
    section.dispatchEvent(new CustomEvent('section:loaded', {
      bubbles: true,
      detail: { sectionId: sectionId, section: section }
    }));

    window.dispatchEvent(new CustomEvent('theme:section:loaded', {
      detail: { sectionId: sectionId, section: section }
    }));

    // Trigger Boost AI recommendations if present
    if (section.querySelector('[id*="boost-sd-widget"]') || section.querySelector('.boost-sd__swiper')) {
      window.dispatchEvent(new CustomEvent('boost-sd:refresh', {
        detail: { section: section }
      }));
    }

    // Refresh ScrollTrigger positions when new section content expands
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      window.ScrollTrigger.refresh();
    }
  }

  // Initialize on DOMContentLoaded and also when Shopify renders dynamically
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSectionLazyLoader);
  } else {
    initSectionLazyLoader();
  }

  // Support Shopify Theme Editor dynamic section events (fired inside admin Theme Customizer when editing)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      loadSection(event.target);
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (event.target instanceof HTMLElement) {
      loadSection(event.target);
    }
  });
})();
